import type { LucideIcon } from "lucide-react";
import {
  CalendarX,
  Receipt,
  ClipboardList,
  UserRound,
  FileText,
  Users,
} from "lucide-react";

/* ────────────────────────────────────────────────────────────────────────
   Notification domain — SINGLE SOURCE OF TRUTH.

   Every notification concept (categories, the types under each category, how a
   category maps to a sidebar tab / route, and its label & icon) is declared
   here. Add or rename a category/type in THIS file and the badge counts, the
   sidebar red-dots and the notification panel all update automatically — no
   other file needs editing.
   ──────────────────────────────────────────────────────────────────────── */

// ── Categories ──────────────────────────────────────────────────────────────
export const NotificationCategory = {
  LEAVE: "LEAVE",
  REIMBURSEMENT: "REIMBURSEMENT",
  TIMESHEET: "TIMESHEET",
  PROFILE: "PROFILE",
  DOCUMENT: "DOCUMENT",
  ACCOUNT: "ACCOUNT",
} as const;
export type NotificationCategory =
  (typeof NotificationCategory)[keyof typeof NotificationCategory];

// ── Types (grouped by the category they belong to) ────────────────────────────
export const NotificationType = {
  // LEAVE
  LEAVE_APPROVAL_PENDING: "LEAVE_APPROVAL_PENDING",
  LEAVE_STATUS_CHANGED: "LEAVE_STATUS_CHANGED",
  // REIMBURSEMENT
  REIMBURSEMENT_PENDING_APPROVAL: "REIMBURSEMENT_PENDING_APPROVAL",
  REIMBURSEMENT_STATUS_CHANGED: "REIMBURSEMENT_STATUS_CHANGED",
  // TIMESHEET
  TIMESHEET_PENDING: "TIMESHEET_PENDING",
  // PROFILE
  PROFILE_INCOMPLETE: "PROFILE_INCOMPLETE",
  // DOCUMENT
  DOCUMENT_REQUIRED: "DOCUMENT_REQUIRED",
  DOCUMENT_REJECTED: "DOCUMENT_REJECTED",
  // ACCOUNT
  NEW_SIGNUP: "NEW_SIGNUP",
  ONBOARDING_REVIEW_PENDING: "ONBOARDING_REVIEW_PENDING",
  ACCOUNT_ACTIVATED: "ACCOUNT_ACTIVATED",
  NEW_USER_ADDED: "NEW_USER_ADDED",
  USER_DEACTIVATED: "USER_DEACTIVATED",
} as const;
export type NotificationType =
  (typeof NotificationType)[keyof typeof NotificationType];

// Maps every type to its owning category. This is the canonical relationship —
// category counts are derived from it, so it stays correct even if the backend
// groups items under a slightly different category key.
export const NOTIFICATION_TYPE_TO_CATEGORY: Record<NotificationType, NotificationCategory> = {
  [NotificationType.LEAVE_APPROVAL_PENDING]: NotificationCategory.LEAVE,
  [NotificationType.LEAVE_STATUS_CHANGED]: NotificationCategory.LEAVE,
  [NotificationType.REIMBURSEMENT_PENDING_APPROVAL]: NotificationCategory.REIMBURSEMENT,
  [NotificationType.REIMBURSEMENT_STATUS_CHANGED]: NotificationCategory.REIMBURSEMENT,
  [NotificationType.TIMESHEET_PENDING]: NotificationCategory.TIMESHEET,
  [NotificationType.PROFILE_INCOMPLETE]: NotificationCategory.PROFILE,
  [NotificationType.DOCUMENT_REQUIRED]: NotificationCategory.DOCUMENT,
  [NotificationType.DOCUMENT_REJECTED]: NotificationCategory.DOCUMENT,
  [NotificationType.NEW_SIGNUP]: NotificationCategory.ACCOUNT,
  [NotificationType.ONBOARDING_REVIEW_PENDING]: NotificationCategory.ACCOUNT,
  [NotificationType.ACCOUNT_ACTIVATED]: NotificationCategory.ACCOUNT,
  [NotificationType.NEW_USER_ADDED]: NotificationCategory.ACCOUNT,
  [NotificationType.USER_DEACTIVATED]: NotificationCategory.ACCOUNT,
};

// ── Category presentation + routing ───────────────────────────────────────────
export interface NotificationCategoryMeta {
  /** Human label shown in the notification panel. */
  label: string;
  /**
   * Sidebar route (must match a `url` in SideBarItems). Drives BOTH which tab
   * shows the red dot AND where clicking a notification navigates. Change here
   * to re-point a category to a different tab.
   */
  sidebarUrl: string;
  /**
   * Optional sub-tab (must match a tab label on the destination page, e.g. the
   * Profile page's "Profile" / "Documents" tabs). When set, clicking a
   * notification deep-links to this sub-tab and the sub-tab shows its own dot.
   */
  subTab?: string;
  icon: LucideIcon;
}

export const NOTIFICATION_CATEGORY_META: Record<NotificationCategory, NotificationCategoryMeta> = {
  [NotificationCategory.LEAVE]: { label: "Leave", sidebarUrl: "leaves", icon: CalendarX },
  [NotificationCategory.REIMBURSEMENT]: { label: "Reimbursement", sidebarUrl: "reimbursement", icon: Receipt },
  [NotificationCategory.TIMESHEET]: { label: "Timesheet", sidebarUrl: "timesheet", icon: ClipboardList },
  // Profile notifications open the Profile tab → "Profile" sub-tab.
  [NotificationCategory.PROFILE]: { label: "Profile", sidebarUrl: "profile", subTab: "Profile", icon: UserRound },
  // Document notifications open the Profile tab → "Documents" sub-tab.
  [NotificationCategory.DOCUMENT]: { label: "Documents", sidebarUrl: "profile", subTab: "Documents", icon: FileText },
  // Account notifications (sign-ups, onboarding review, activation, de-activation)
  // are admin-facing and surface on the Employees tab.
  [NotificationCategory.ACCOUNT]: { label: "Account", sidebarUrl: "employees", icon: Users },
};

// Per-type presentation + optional deep-link.
// - title: the notification headline (the API `message` becomes the body).
//   Falls back to the category label when a type has no entry.
// - queryParams: extra query appended to the category's destination route when
//   THIS specific type is clicked, e.g. ONBOARDING_REVIEW_PENDING opens the
//   Employees screen pre-filtered to onboarding users (?status=ONBOARDING).
export interface NotificationTypeMeta {
  title: string;
  queryParams?: Record<string, string>;
}

export const NOTIFICATION_TYPE_META: Record<NotificationType, NotificationTypeMeta> = {
  [NotificationType.LEAVE_APPROVAL_PENDING]: { title: "Leave approval pending" },
  [NotificationType.LEAVE_STATUS_CHANGED]: { title: "Leave status updated" },
  [NotificationType.REIMBURSEMENT_PENDING_APPROVAL]: { title: "Reimbursement approval pending" },
  [NotificationType.REIMBURSEMENT_STATUS_CHANGED]: { title: "Reimbursement status updated" },
  [NotificationType.TIMESHEET_PENDING]: { title: "Timesheet pending" },
  [NotificationType.PROFILE_INCOMPLETE]: { title: "Complete your profile" },
  [NotificationType.DOCUMENT_REQUIRED]: { title: "Document required" },
  [NotificationType.DOCUMENT_REJECTED]: { title: "Document rejected" },
  [NotificationType.NEW_SIGNUP]: { title: "New sign-up" },
  // Admin clicks this → Employees screen filtered to onboarding users.
  [NotificationType.ONBOARDING_REVIEW_PENDING]: {
    title: "Onboarding awaiting review",
    queryParams: { status: "ONBOARDING" },
  },
  [NotificationType.ACCOUNT_ACTIVATED]: { title: "Account activated" },
  [NotificationType.NEW_USER_ADDED]: { title: "New team member added" },
  [NotificationType.USER_DEACTIVATED]: { title: "Team member deactivated" },
};

// Stable display order for the notification panel.
export const NOTIFICATION_CATEGORY_ORDER: NotificationCategory[] = [
  NotificationCategory.ACCOUNT,
  NotificationCategory.LEAVE,
  NotificationCategory.REIMBURSEMENT,
  NotificationCategory.TIMESHEET,
  NotificationCategory.DOCUMENT,
  NotificationCategory.PROFILE,
];

// ── API response shape: GET /api/notifications/badge ──────────────────────────
export interface NotificationBadgeItem {
  type: NotificationType | string;
  count: number;
  action: boolean;
  message: string;
}

export interface NotificationBadgeResponse {
  totalCount: number;
  // Keyed by category string (e.g. "LEAVE"). Left as `string` so an unexpected
  // key from the backend never breaks parsing.
  categories: Record<string, NotificationBadgeItem[]>;
}

// ── Derived selectors (pure, reused by hook / sidebar / panel) ────────────────

// Normalises a raw category key from the response to a known category (tolerant
// of pluralisation / casing, e.g. "ACCOUNTS" → ACCOUNT).
const coerceCategoryKey = (key: string): NotificationCategory | undefined => {
  const upper = (key || "").toUpperCase();
  if (upper in NOTIFICATION_CATEGORY_META) return upper as NotificationCategory;
  if (upper === "ACCOUNTS") return NotificationCategory.ACCOUNT;
  // Legacy key, kept so an older "EMPLOYEE(S)" payload still maps to Account.
  if (upper === "EMPLOYEE" || upper === "EMPLOYEES") return NotificationCategory.ACCOUNT;
  if (upper === "TIMESHEETS") return NotificationCategory.TIMESHEET;
  if (upper === "DOCUMENTS") return NotificationCategory.DOCUMENT;
  return undefined;
};

// Resolves an item's canonical category from its `type` (preferred), falling
// back to the response's category key.
const resolveItemCategory = (
  item: NotificationBadgeItem,
  fallbackKey: string,
): NotificationCategory | undefined =>
  NOTIFICATION_TYPE_TO_CATEGORY[item.type as NotificationType] ?? coerceCategoryKey(fallbackKey);

/** Total count across the whole payload (uses server total, falls back to a sum). */
export const getNotificationTotal = (res: NotificationBadgeResponse | null): number => {
  if (!res) return 0;
  if (typeof res.totalCount === "number") return res.totalCount;
  return Object.values(res.categories || {})
    .flat()
    .reduce((sum, item) => sum + (Number(item?.count) || 0), 0);
};

/** Per-category totals, robustly bucketed by each item's type. */
export const getCategoryCounts = (
  res: NotificationBadgeResponse | null,
): Record<NotificationCategory, number> => {
  const counts = Object.fromEntries(
    Object.values(NotificationCategory).map((c) => [c, 0]),
  ) as Record<NotificationCategory, number>;
  if (!res?.categories) return counts;

  Object.entries(res.categories).forEach(([key, items]) => {
    (items || []).forEach((item) => {
      const category = resolveItemCategory(item, key);
      if (category) counts[category] += Number(item?.count) || 0;
    });
  });
  return counts;
};

/**
 * Notification types for a sidebar route that should be auto-marked read when the
 * user opens that tab: INFORMATIONAL items only (action === false) with count > 0.
 *
 * Action-required items (action === true) are intentionally excluded — those need
 * the user to actually do something (e.g. approve a leave), so they must stay
 * flagged until resolved rather than clearing just because the tab was visited.
 *
 * Multiple categories can share one sidebarUrl (e.g. PROFILE + DOCUMENT → profile),
 * so types are collected across every category mapped to the given url.
 */
export const getAutoReadTypesForSidebarUrl = (
  res: NotificationBadgeResponse | null,
  sidebarUrl: string,
): string[] => {
  if (!res?.categories || !sidebarUrl) return [];
  const types = new Set<string>();
  Object.entries(res.categories).forEach(([key, items]) => {
    (items || []).forEach((item) => {
      const category = resolveItemCategory(item, key);
      if (!category) return;
      if (NOTIFICATION_CATEGORY_META[category].sidebarUrl !== sidebarUrl) return;
      if (item.action === true) return; // needs explicit user action — keep it
      if ((Number(item?.count) || 0) <= 0) return;
      if (item.type) types.add(String(item.type));
    });
  });
  return Array.from(types);
};

/** Sidebar routes that should show a red dot (categories with count > 0). */
export const getFlaggedSidebarUrls = (res: NotificationBadgeResponse | null): Set<string> => {
  const counts = getCategoryCounts(res);
  const urls = new Set<string>();
  (Object.keys(counts) as NotificationCategory[]).forEach((category) => {
    if (counts[category] > 0) urls.add(NOTIFICATION_CATEGORY_META[category].sidebarUrl);
  });
  return urls;
};

/**
 * Sub-tab labels on a given page (sidebarUrl) that should show a dot — i.e. every
 * category mapped to that url whose count > 0 and that declares a `subTab`.
 * e.g. for "profile" → {"Profile"} and/or {"Documents"}.
 */
export const getFlaggedSubTabs = (
  res: NotificationBadgeResponse | null,
  sidebarUrl: string,
): Set<string> => {
  const counts = getCategoryCounts(res);
  const subTabs = new Set<string>();
  (Object.keys(counts) as NotificationCategory[]).forEach((category) => {
    const meta = NOTIFICATION_CATEGORY_META[category];
    if (counts[category] > 0 && meta.sidebarUrl === sidebarUrl && meta.subTab) {
      subTabs.add(meta.subTab);
    }
  });
  return subTabs;
};

// One category's grouped notifications, ready for the panel.
export interface NotificationGroup {
  category: NotificationCategory;
  meta: NotificationCategoryMeta;
  count: number;
  actionRequired: boolean;
  items: NotificationBadgeItem[];
}

/** Groups the payload by category (in display order) for the notification panel. */
export const getNotificationGroups = (
  res: NotificationBadgeResponse | null,
): NotificationGroup[] => {
  if (!res?.categories) return [];

  const buckets = new Map<NotificationCategory, NotificationBadgeItem[]>();
  Object.entries(res.categories).forEach(([key, items]) => {
    (items || []).forEach((item) => {
      const category = resolveItemCategory(item, key);
      if (!category) return;
      const bucket = buckets.get(category) ?? [];
      bucket.push(item);
      buckets.set(category, bucket);
    });
  });

  // Known categories first (in order), then any unexpected ones.
  const ordered = [
    ...NOTIFICATION_CATEGORY_ORDER,
    ...[...buckets.keys()].filter((c) => !NOTIFICATION_CATEGORY_ORDER.includes(c)),
  ];

  return ordered
    .map((category) => ({ category, items: buckets.get(category) }))
    .filter((entry): entry is { category: NotificationCategory; items: NotificationBadgeItem[] } =>
      Array.isArray(entry.items) && entry.items.length > 0,
    )
    .map(({ category, items }) => ({
      category,
      meta: NOTIFICATION_CATEGORY_META[category],
      count: items.reduce((sum, item) => sum + (Number(item?.count) || 0), 0),
      actionRequired: items.some((item) => item.action === true),
      items,
    }));
};

// A single, flattened notification ready to render as one list row: resolved
// title + message + count + its category icon/route.
export interface NotificationListItem {
  key: string;
  category: NotificationCategory;
  meta: NotificationCategoryMeta;
  type: string;
  title: string;
  message: string;
  count: number;
  action: boolean;
  /** Type-specific query params to apply at the destination (e.g. { status: "ONBOARDING" }). */
  query?: Record<string, string>;
}

/**
 * Flat, de-duplicated list for the notification panel — one row per badge item,
 * action-required items first, then in category order.
 */
export const getNotificationItems = (
  res: NotificationBadgeResponse | null,
): NotificationListItem[] => {
  if (!res?.categories) return [];

  const items: NotificationListItem[] = [];
  Object.entries(res.categories).forEach(([key, arr]) => {
    (arr || []).forEach((item, index) => {
      const category = resolveItemCategory(item, key);
      if (!category) return;
      const meta = NOTIFICATION_CATEGORY_META[category];
      items.push({
        key: `${category}-${item.type}-${index}`,
        category,
        meta,
        type: String(item.type),
        title: NOTIFICATION_TYPE_META[item.type as NotificationType]?.title ?? meta.label,
        message: item.message,
        count: Number(item?.count) || 0,
        action: item.action === true,
        query: NOTIFICATION_TYPE_META[item.type as NotificationType]?.queryParams,
      });
    });
  });

  const orderOf = (category: NotificationCategory) => {
    const index = NOTIFICATION_CATEGORY_ORDER.indexOf(category);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };

  return items.sort(
    (a, b) => Number(b.action) - Number(a.action) || orderOf(a.category) - orderOf(b.category),
  );
};
